import { useEffect, useId, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';

import { cx } from '../cx';
import { controlClass } from './Field';

// O `controlClass` traz `w-full`; aqui cada campo diz a largura que quer (sem
// isto, na data + hora, a hora ocupava tudo e a data ficava espremida).
const baseControl = controlClass.replace(/(^|\s)w-full(?=\s|$)/, ' ').trim();

// Campos de data e hora da Grow uP. Os `<input type="date">` nativos seguem a
// lingua do navegador (num Chrome em ingles aparecem mm/dd/aaaa e AM/PM), por
// isso estes mostram sempre dd/mm/aaaa e 24 h. O valor trocado com a app e o
// mesmo dos nativos: `aaaa-mm-dd`, `hh:mm` e `aaaa-mm-ddThh:mm`.

const LISBON = 'Europe/Lisbon';
const isoDay = new Intl.DateTimeFormat('en-CA', {
  timeZone: LISBON,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** O dia de hoje em Lisboa, `aaaa-mm-dd`, seja qual for o fuso do PC. */
export const todayInLisbon = (now: Date = new Date()) => isoDay.format(now);

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const WEEKDAYS = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom'];
const WEEKDAYS_LONG = [
  'segunda-feira',
  'terça-feira',
  'quarta-feira',
  'quinta-feira',
  'sexta-feira',
  'sábado',
  'domingo',
];

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (y: number, m: number, d: number) => `${String(y)}-${pad(m + 1)}-${pad(d)}`;
const daysIn = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
/** 0 = segunda ... 6 = domingo. */
const weekday = (y: number, m: number, d: number) =>
  (new Date(Date.UTC(y, m, d)).getUTCDay() + 6) % 7;

function splitIso(iso: string): [number, number, number] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const [y, mo, d] = m.slice(1).map(Number) as [number, number, number];
  if (mo < 1 || mo > 12 || d < 1 || d > daysIn(y, mo - 1)) return null;
  return [y, mo - 1, d];
}

function addDays(iso: string, days: number): string {
  const p = splitIso(iso);
  if (!p) return iso;
  const date = new Date(Date.UTC(p[0], p[1], p[2] + days));
  return toIso(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/** `2026-10-18` → `18/10/2026`. Vazio se o valor nao for uma data. */
export function isoToDisplayDate(iso: string): string {
  const p = splitIso(iso);
  return p ? `${pad(p[2])}/${pad(p[1] + 1)}/${String(p[0])}` : '';
}

/** `18/10/2026` → `2026-10-18`; `null` se estiver incompleta ou nao existir. */
export function displayToIsoDate(text: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (!m) return null;
  const [d, mo, y] = m.slice(1).map(Number) as [number, number, number];
  const iso = toIso(y, mo - 1, d);
  return splitIso(iso) ? iso : null;
}

/**
 * So os algarismos de uma data escrita. Uma data completa com separadores
 * (`1/2/2026`, `18.10.2026`, `2026-10-18`) entra como esta. Enquanto se escreve
 * no fim do campo, o dia e o mes levam um zero a frente quando se escreve a
 * barra (`1/` → `01`) ou quando o algarismo ja nao pode ter segundo (`5` → `05`,
 * mes `3` → `03`). A corrigir no meio, nao se mexe em nada alem das barras.
 */
function dateDigits(raw: string, typing: boolean): string {
  const iso = /^\s*(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return `${iso[3] ?? ''}${iso[2] ?? ''}${iso[1] ?? ''}`;
  const full = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\s*$/.exec(raw);
  if (full)
    return `${(full[1] ?? '').padStart(2, '0')}${(full[2] ?? '').padStart(2, '0')}${full[3] ?? ''}`;
  let digits = raw.replace(/\D/g, '');
  if (typing) {
    const sep = /(?:^|[/.-])(\d)[/.-]$/.exec(raw);
    if (sep && digits.length <= 3) digits = `${digits.slice(0, -1)}0${digits.slice(-1)}`;
    if (/^[4-9]/.test(digits)) digits = `0${digits}`;
    if (digits.length === 3 && /^[2-9]$/.test(digits.slice(2))) {
      digits = `${digits.slice(0, 2)}0${digits.slice(2)}`;
    }
  }
  return digits.slice(0, 8);
}

/** Mete as barras enquanto se escreve: `1810` → `18/10`. Aceita colar `aaaa-mm-dd`. */
function maskDate(raw: string, typing = true): string {
  // A corrigir no meio fica o que se escreveu; arruma-se ao sair do campo.
  if (!typing) return raw.replace(/[^\d/]/g, '').slice(0, 10);
  const digits = dateDigits(raw, typing);
  let out = digits.slice(0, 2);
  if (digits.length > 2) out += `/${digits.slice(2, 4)}`;
  if (digits.length > 4) out += `/${digits.slice(4)}`;
  return out;
}

/** Algarismos de uma hora escrita: `9:30`, `9h30` e `930` dao `0930`. */
function timeDigits(raw: string, typing: boolean): string {
  const typed = /^\s*(\d{1,2})[:h.]\s*(\d{0,2})\s*$/.exec(raw);
  if (typed) return `${(typed[1] ?? '').padStart(2, '0')}${typed[2] ?? ''}`;
  let digits = raw.replace(/\D/g, '');
  // Uma hora nao comeca por 3 a 9 seguido de outro algarismo: e `9` e nao `93`.
  if (typing && /^[3-9]/.test(digits)) digits = `0${digits}`;
  return digits.slice(0, 4);
}

/** Mete os dois pontos: `0930` → `09:30`, `9:30` → `09:30`. */
function maskTime(raw: string, typing = true): string {
  if (!typing) return raw.replace(/[^\d:]/g, '').slice(0, 5);
  const digits = timeDigits(raw, typing);
  return digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
}

/** Ao sair do campo, `9` fica `09:00`, `14` fica `14:00` e `9:30` fica `09:30`. */
function finishTime(text: string): string {
  if (/^\d{1,2}$/.test(text)) return `${text.padStart(2, '0')}:00`;
  return validTime(text) ?? text;
}

/** Ao sair do campo, `1/2/2026` fica `01/02/2026`. */
function finishDate(text: string): string {
  const iso = displayToIsoDate(text);
  return iso ? isoToDisplayDate(iso) : text;
}

function validTime(text: string): string | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const [h, min] = [Number(m[1]), Number(m[2])];
  return h < 24 && min < 60 ? `${pad(h)}:${pad(min)}` : null;
}

type A11yProps = {
  id?: string | undefined;
  'aria-describedby'?: string | undefined;
  'aria-invalid'?: boolean | undefined;
  'aria-label'?: string | undefined;
};

/**
 * Texto com mascara que so avisa a app quando o que esta escrito e valido (ou
 * ficou vazio). Enquanto se escreve, a app recebe `''`; o rascunho fica aqui.
 */
function useMaskedDraft(
  value: string,
  onChange: (value: string) => void,
  toText: (value: string) => string,
  toValue: (text: string) => string | null,
  mask: (raw: string, typing: boolean) => string,
  finish: (text: string) => string = (t) => t,
) {
  const [text, setText] = useState(() => toText(value));
  const [touched, setTouched] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  // O valor de fora mudou (carregou da API, «Hoje», reset): o texto acompanha.
  useEffect(() => {
    setText((current) => ((toValue(current) ?? '') === value ? current : toText(value)));
  }, [value, toText, toValue]);

  const commit = (next: string) => {
    setText(next);
    const nextValue = toValue(next) ?? '';
    if (nextValue !== value) onChange(nextValue);
  };
  const parsed = toValue(text);
  const invalid = touched && text.trim() !== '' && parsed === null;
  return {
    ref: input,
    text,
    invalid,
    onInput(e: ChangeEvent<HTMLInputElement>) {
      const raw = e.target.value;
      const at = e.target.selectionStart ?? raw.length;
      // So se completa (barras, zeros) quando se escreve no fim do campo; a
      // corrigir no meio, mexer no texto fazia saltar algarismos e o cursor.
      commit(mask(raw, at >= raw.length));
    },
    onBlur() {
      setTouched(true);
      const done = finish(text);
      if (done !== text) commit(done);
    },
  };
}

export type DateInputProps = A11yProps & {
  /** `aaaa-mm-dd`, ou `''` sem data. */
  value: string;
  onChange: (value: string) => void;
  /** Limites, `aaaa-mm-dd`. Os dias fora ficam desativados no calendario. */
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
  /** Para o navegador sugerir a data guardada (ex.: `bday`). */
  autoComplete?: string | undefined;
  className?: string | undefined;
};

/** Data em dd/mm/aaaa, com calendario em portugues e os botoes «Hoje» e «Limpar». */
export function DateInput({
  value,
  onChange,
  min,
  max,
  disabled,
  autoComplete,
  className,
  ...a11y
}: DateInputProps) {
  const draft = useMaskedDraft(
    value,
    onChange,
    isoToDisplayDate,
    displayToIsoDate,
    maskDate,
    finishDate,
  );
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Placement>({ up: false, right: false });
  const wrapper = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const outOfRange = value !== '' && ((min && value < min) || (max && value > max));

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    toggle.current?.focus();
  };
  const pick = (iso: string) => {
    if (iso !== value) onChange(iso);
    close();
  };

  return (
    <div ref={wrapper} className={cx('relative flex', className)}>
      <input
        {...a11y}
        ref={draft.ref}
        aria-invalid={a11y['aria-invalid'] || draft.invalid || Boolean(outOfRange) || undefined}
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        maxLength={10}
        autoComplete={autoComplete ?? 'off'}
        disabled={disabled}
        value={draft.text}
        onChange={draft.onInput}
        onBlur={draft.onBlur}
        className={cx(baseControl, 'min-h-11 w-full min-w-0 flex-1 tabular-nums')}
      />
      <button
        ref={toggle}
        type="button"
        disabled={disabled}
        aria-label="Abrir calendário"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (!open && wrapper.current) setPlace(placeCalendar(wrapper.current));
          setOpen((o) => !o);
        }}
        className="min-h-11 shrink-0 border-2 border-l-0 border-gu-line bg-gu-sunken px-3 text-gu-ink-muted transition-colors hover:text-gu-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus/40 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <CalendarIcon />
      </button>
      {open ? (
        <Calendar
          value={splitIso(value) ? value : ''}
          min={min}
          max={max}
          place={place}
          onPick={pick}
          onClear={() => {
            if (value !== '') onChange('');
            close();
          }}
          onClose={close}
        />
      ) : null}
    </div>
  );
}

type Placement = { up: boolean; right: boolean };

const CALENDAR_WIDTH = 288;
const CALENDAR_HEIGHT = 330;

/**
 * Abre para cima quando nao cabe por baixo (fundo do ecra, teclado do
 * telemovel) e encosta a direita quando passaria a borda do ecra.
 */
function placeCalendar(anchor: HTMLElement): Placement {
  const box = anchor.getBoundingClientRect();
  const below = window.innerHeight - box.bottom;
  return {
    up: below < CALENDAR_HEIGHT && box.top > below,
    right: box.left + CALENDAR_WIDTH > window.innerWidth - 8 && box.right >= CALENDAR_WIDTH,
  };
}

type CalendarProps = {
  value: string;
  place: Placement;
  min?: string | undefined;
  max?: string | undefined;
  onPick: (iso: string) => void;
  onClear: () => void;
  onClose: () => void;
};

function Calendar({ value, min, max, place, onPick, onClear, onClose }: CalendarProps) {
  const today = todayInLisbon();
  const start = value || (max && today > max ? max : min && today < min ? min : today);
  const [focus, setFocus] = useState(start);
  const [fy, fm] = splitIso(focus) ?? [2000, 0];
  const grid = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Ao abrir, o foco vai para o dia escolhido; depois so segue as setas, para
  // nao o tirar dos botoes de mes e das listas.
  const followFocus = useRef(true);

  useEffect(() => {
    if (!followFocus.current) return;
    followFocus.current = false;
    grid.current
      ?.querySelector<HTMLButtonElement>(`[data-day="${focus}"]`)
      ?.focus({ preventScroll: true });
  }, [focus]);

  const blocked = (iso: string) => Boolean((min && iso < min) || (max && iso > max));
  const todayYear = Number(today.slice(0, 4));
  const firstYear = min ? Number(min.slice(0, 4)) : Math.min(todayYear - 100, fy);
  const lastYear = max ? Number(max.slice(0, 4)) : Math.max(todayYear + 10, fy);
  const years: number[] = [];
  for (let y = lastYear; y >= firstYear; y--) years.push(y);

  const goTo = (y: number, m: number) => {
    const [, , d] = splitIso(focus) ?? [y, m, 1];
    setFocus(toIso(y, m, Math.min(d, daysIn(y, m))));
  };
  const shiftMonth = (delta: number) => {
    const total = fy * 12 + fm + delta;
    goTo(Math.floor(total / 12), ((total % 12) + 12) % 12);
  };

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    if (e.key in step) {
      e.preventDefault();
      followFocus.current = true;
      setFocus(addDays(focus, step[e.key] ?? 0));
    } else if (e.key === 'PageUp' || e.key === 'PageDown') {
      e.preventDefault();
      followFocus.current = true;
      shiftMonth(e.key === 'PageUp' ? -1 : 1);
    }
  };

  const lead = weekday(fy, fm, 1);
  const cells: (number | null)[] = [
    ...Array<null>(lead).fill(null),
    ...Array.from({ length: daysIn(fy, fm) }, (_, i) => i + 1),
  ];
  while (cells.length % 7) cells.push(null);

  const select = 'border border-gu-line bg-gu-sunken px-1 py-1 text-caption text-gu-ink';
  const nav =
    'size-8 shrink-0 border border-gu-line text-gu-ink hover:bg-gu-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus/40';

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
      }}
      className={cx(
        'absolute z-50 w-72 max-w-[calc(100vw-2rem)] border-2 border-gu-line bg-gu-raised p-3 text-gu-ink shadow-lg',
        place.up ? 'bottom-full mb-1' : 'top-full mt-1',
        place.right ? 'right-0' : 'left-0',
      )}
    >
      <span id={titleId} className="sr-only">
        Escolher data, {MONTHS[fm]} de {String(fy)}
      </span>
      <div className="mb-2 flex items-center gap-1">
        <button
          type="button"
          className={nav}
          aria-label="Mês anterior"
          onClick={() => {
            shiftMonth(-1);
          }}
        >
          ‹
        </button>
        <select
          aria-label="Mês"
          className={cx(select, 'flex-1')}
          value={fm}
          onChange={(e) => {
            goTo(fy, Number(e.target.value));
          }}
        >
          {MONTHS.map((name, i) => (
            <option key={name} value={i}>
              {name}
            </option>
          ))}
        </select>
        <select
          aria-label="Ano"
          className={select}
          value={fy}
          onChange={(e) => {
            goTo(Number(e.target.value), fm);
          }}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={nav}
          aria-label="Mês seguinte"
          onClick={() => {
            shiftMonth(1);
          }}
        >
          ›
        </button>
      </div>
      <div ref={grid} role="grid" aria-labelledby={titleId} onKeyDown={onKey}>
        <div role="row" className="grid grid-cols-7">
          {WEEKDAYS.map((d, i) => (
            <span
              key={d}
              role="columnheader"
              aria-label={WEEKDAYS_LONG[i]}
              className="py-1 text-center text-caption uppercase text-gu-ink-muted"
            >
              {d}
            </span>
          ))}
        </div>
        {Array.from({ length: cells.length / 7 }, (_, row) => (
          <div key={row} role="row" className="grid grid-cols-7">
            {cells.slice(row * 7, row * 7 + 7).map((day, col) => {
              if (day === null) return <span key={`e${String(col)}`} role="gridcell" />;
              const iso = toIso(fy, fm, day);
              const chosen = iso === value;
              return (
                <span key={iso} role="gridcell" aria-selected={chosen}>
                  <button
                    type="button"
                    data-day={iso}
                    tabIndex={iso === focus ? 0 : -1}
                    disabled={blocked(iso)}
                    aria-label={`${String(day)} de ${MONTHS[fm] ?? ''} de ${String(fy)}`}
                    aria-current={iso === today ? 'date' : undefined}
                    onClick={() => {
                      onPick(iso);
                    }}
                    className={cx(
                      'h-9 w-full text-body tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gu-focus',
                      chosen
                        ? 'bg-gu-accent font-bold text-gu-on-accent'
                        : 'hover:bg-gu-sunken disabled:cursor-not-allowed disabled:opacity-30',
                      iso === today && !chosen && 'font-bold text-gu-accent underline',
                    )}
                  >
                    {day}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between gap-2 border-t border-gu-line pt-2">
        <button
          type="button"
          onClick={onClear}
          className="px-2 py-1 font-sub text-label font-bold uppercase text-gu-ink-muted hover:text-gu-ink"
        >
          Limpar
        </button>
        <button
          type="button"
          disabled={blocked(today)}
          onClick={() => {
            onPick(today);
          }}
          className="bg-gu-accent px-3 py-1 font-sub text-label font-bold uppercase text-gu-on-accent hover:bg-gu-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          Hoje
        </button>
      </div>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="size-5"
      fill="none"
      stroke="currentColor"
    >
      <rect x="3" y="4.5" width="14" height="12" strokeWidth="1.6" />
      <path d="M3 8.5h14M7 2.5v4M13 2.5v4" strokeWidth="1.6" />
    </svg>
  );
}

const sameText = (v: string) => v;

export type TimeInputProps = A11yProps & {
  /** `hh:mm` em 24 h, ou `''`. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
};

/** Hora em 24 h (hh:mm), igual em qualquer navegador. */
export function TimeInput({ value, onChange, disabled, className, ...a11y }: TimeInputProps) {
  const draft = useMaskedDraft(value, onChange, sameText, validTime, maskTime, finishTime);
  return (
    <input
      {...a11y}
      ref={draft.ref}
      aria-invalid={a11y['aria-invalid'] || draft.invalid || undefined}
      type="text"
      inputMode="numeric"
      placeholder="hh:mm"
      maxLength={5}
      autoComplete="off"
      disabled={disabled}
      value={draft.text}
      onChange={draft.onInput}
      onBlur={draft.onBlur}
      className={cx(baseControl, 'min-h-11 w-[5.5rem] shrink-0 tabular-nums', className)}
    />
  );
}

export type DateTimeInputProps = A11yProps & {
  /** `aaaa-mm-ddThh:mm` (o formato do `datetime-local`), ou `''` enquanto falta uma parte. */
  value: string;
  onChange: (value: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
  className?: string | undefined;
};

/** Data e hora lado a lado. A app so recebe um valor quando as duas partes estao preenchidas. */
export function DateTimeInput({
  value,
  onChange,
  min,
  max,
  disabled,
  className,
  ...a11y
}: DateTimeInputProps) {
  const [date, setDate] = useState(value.slice(0, 10));
  const [time, setTime] = useState(value.slice(11, 16));
  const combined = date && time ? `${date}T${time}` : '';
  useEffect(() => {
    if (value !== combined) {
      setDate(value.slice(0, 10));
      setTime(value.slice(11, 16));
    }
    // So quando muda o valor de fora; o combinado acompanha as partes.
  }, [value]);

  const emit = (d: string, t: string) => {
    const next = d && t ? `${d}T${t}` : '';
    if (next !== value) onChange(next);
  };

  return (
    <div className={cx('flex flex-wrap gap-2', className)}>
      <DateInput
        {...a11y}
        className="min-w-[10rem] flex-1"
        value={date}
        min={min?.slice(0, 10)}
        max={max?.slice(0, 10)}
        disabled={disabled}
        onChange={(d) => {
          setDate(d);
          emit(d, time);
        }}
      />
      <TimeInput
        aria-label="Hora"
        aria-describedby={a11y['aria-describedby']}
        aria-invalid={a11y['aria-invalid']}
        value={time}
        disabled={disabled}
        onChange={(t) => {
          setTime(t);
          emit(date, t);
        }}
      />
    </div>
  );
}
